import { useState } from "react";

import { ListFilter } from "lucide-react";

import { useAppForm } from "@/components/Form";

import { Button } from "@components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";

import { cn } from "@utils/ui";

import { ACTION_OPTIONS, USER_TYPE_OPTIONS } from "../constants";
import { AuditLogFilterFormData } from "../schema/filters";

export type AuditLogFilters = {
  action?: string;
  userType?: string;
};

type FilterProps = {
  className?: string;
  onFiltersChange?: (filters: AuditLogFilters) => void;
};

export const Filter = ({ className, onFiltersChange }: FilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  const form = useAppForm({
    defaultValues: {
      action: "",
      userType: "",
    } as AuditLogFilterFormData,
    onSubmit: async ({ value }) => {
      onFiltersChange?.({
        action: value.action || undefined,
        userType: value.userType || undefined,
      });

      const activeFilters = Object.values(value).some(
        (val) => typeof val === "string" && val.trim() !== ""
      );

      setHasActiveFilters(activeFilters);
      setIsOpen(false);
    },
  });

  const handleCancel = () => {
    form.reset();
    onFiltersChange?.({});
    setHasActiveFilters(false);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          {/* Gray simple-outline by default (matches Cancel); switches to the
              blue secondary-subtle variant once filters are active — matching
              CasesList's/BookingApprovals's Filter trigger. */}
          <Button
            variant={hasActiveFilters ? "secondary-subtle" : "simple-outline"}
            size="sm"
            className={cn("h-10 gap-2 transition-all duration-150", className)}
          >
            <ListFilter className="size-4" />
            Filter
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="flex max-h-[var(--radix-popover-content-available-height)] w-80 flex-col overflow-hidden rounded-xl border border-border bg-background-white p-0 shadow-lg"
          align="end"
          side="bottom"
          sideOffset={8}
        >
          <h3 className="flex-shrink-0 border-b border-border px-4 pb-2 pt-4 text-sm font-semibold">
            Filter
          </h3>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-2">
              <form.AppField name="action">
                {(field) => (
                  <field.SelectField
                    label="Action"
                    labelClassName="text-xs"
                    labelContainerClassName="mb-1"
                    placeholder="All actions"
                    options={ACTION_OPTIONS}
                  />
                )}
              </form.AppField>

              <form.AppField name="userType">
                {(field) => (
                  <field.SelectField
                    label="User type"
                    labelClassName="text-xs"
                    labelContainerClassName="mb-1"
                    placeholder="All users"
                    options={USER_TYPE_OPTIONS}
                  />
                )}
              </form.AppField>
            </div>

            <form.AppForm>
              <div className="flex flex-shrink-0 items-center justify-between gap-3 border-t border-border px-4 py-4">
                <button
                  type="button"
                  className="font-head text-xs font-semibold text-foreground-muted underline-offset-2 hover:text-foreground hover:underline"
                  onClick={handleCancel}
                >
                  Clear all
                </button>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="simple-outline"
                    size="sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <form.SubmitButton size="sm">Apply filters</form.SubmitButton>
                </div>
              </div>
            </form.AppForm>
          </form>
        </PopoverContent>
      </Popover>
    </div>
  );
};
